import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';
import { useCharacterSheets } from '../hooks/useCharacterSheets';
import { useTemplates } from '../hooks/useTemplates';
import { usePopout } from '../hooks/usePopout';
import { useSessionContext } from './SessionContext';

type UseCharacterSheetsReturn = ReturnType<typeof useCharacterSheets>;
type UseTemplatesReturn = ReturnType<typeof useTemplates>;
type UsePopoutReturn = ReturnType<typeof usePopout>;

export interface SheetContextValue {
  sheets: UseCharacterSheetsReturn;
  templates: UseTemplatesReturn;
  popout: UsePopoutReturn;
  onSendSheet: (msg: any) => void;
}

const SheetContext = createContext<SheetContextValue | null>(null);

export function SheetProvider({ children }: { children: ReactNode }) {
  const { mp } = useSessionContext();
  // Pass the local client's identity so public-fields updates never collapse
  // the owner's own sheet (see useCharacterSheets.handlePublicFieldsUpdated).
  const sheets = useCharacterSheets({
    participantName: mp.participant?.name,
    userId: mp.participant?.userId,
  });
  const templates = useTemplates();
  const popout = usePopout();

  const onSendSheet = useCallback(
    (msg: any) => {
      if (mp.session) mp.send(msg);
    },
    [mp],
  );

  const value = useMemo(
    () => ({ sheets, templates, popout, onSendSheet }),
    [sheets, templates, popout, onSendSheet],
  );

  return (
    <SheetContext.Provider value={value}>
      {children}
    </SheetContext.Provider>
  );
}

export function useSheetContext() {
  const ctx = useContext(SheetContext);
  if (!ctx) throw new Error('useSheetContext must be used within SheetProvider');
  return ctx;
}

/**
 * Non-throwing variant for components that ALSO render outside the session
 * tree (demo mode, unit tests render sections without providers). Returns
 * null there; callers degrade to their providerless behavior.
 */
export function useOptionalSheetContext(): SheetContextValue | null {
  return useContext(SheetContext);
}
